import { useCallback, useState } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { v4 as uuid } from 'uuid';
import { getContainer } from '@framework/di/getContainer';
import AskAiChatSubscriptionRepository from '@/creditflowresearch/infrastructure/repository/AskAiChatSubscriptionRepository';
import {
  IAskAiAnswerPayload,
  IAskAiCancelledPayload,
  IAskAiChatMessage,
  IAskAiDonePayload,
  IAskAiErrorPayload,
  IAskAiProcessingPayload,
} from '@/creditflowresearch/domain/vo/AskAiChat';
import CurrentAnswer from '../helpers/currentAnswer';
import {
  addAssistantMessage,
  appendQuestion,
  applyAnswer,
  applyCancelled,
  applyDone,
  applyError,
  applyFailure,
  applyProcessing,
  renamePlaceholder,
} from '../helpers/chatMessages';
import useAskAiChatSubscription from './useAskAiChatSubscription';
import useAnswerTimeout from './useAnswerTimeout';
import { ROWS_PER_ANSWER } from '../constants';

const TIMEOUT_MESSAGE = 'The request timed out. Please try again.';

interface IAnswerEvent {
  chatId: string,
  messageId?: string,
}

interface IParams {
  isEnabled: boolean,
  chatIdRef: RefObject<string | null>,
  promptVersionIdRef: RefObject<string | null>,
  setChatId: (id: string | null) => void,
  setMessages: Dispatch<SetStateAction<IAskAiChatMessage[]>>,
  setConnectionError: (message: string | null) => void,
  onChatCreated: () => void,
  onBackgroundAnswerDone: (chatId: string) => void,
}

const useAskAiChatAnswer = ({
  isEnabled,
  chatIdRef,
  promptVersionIdRef,
  setChatId,
  setMessages,
  setConnectionError,
  onChatCreated,
  onBackgroundAnswerDone,
}: IParams) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentAnswer] = useState(() => new CurrentAnswer());
  const [backgroundChatIds] = useState(() => new Set<string>());

  const subscription = getContainer().get<AskAiChatSubscriptionRepository>(AskAiChatSubscriptionRepository);

  const isAnswerInProgress = useCallback((): boolean => {
    const chatId = chatIdRef.current;

    return currentAnswer.isRunning || (!!chatId && backgroundChatIds.has(chatId));
  }, [backgroundChatIds, chatIdRef, currentAnswer]);

  const cancel = useCallback((): void => {
    if (isAnswerInProgress() && chatIdRef.current) {
      subscription.cancel(chatIdRef.current);
    }
  }, [chatIdRef, isAnswerInProgress, subscription]);

  const finishAnswer = useCallback((): void => {
    const isChatCreated = currentAnswer.isNewChat && !!chatIdRef.current;

    currentAnswer.stop();
    setIsGenerating(false);

    if (isChatCreated) {
      onChatCreated();
    }
  }, [chatIdRef, currentAnswer, onChatCreated]);

  const failAnswer = (errorText: string): void => {
    const { placeholderId } = currentAnswer;

    setMessages((prev) => applyFailure(prev, placeholderId, errorText));
    finishAnswer();
  };

  const onTimeout = (): void => {
    if (!currentAnswer.isRunning) {
      return;
    }

    cancel();

    if (!chatIdRef.current) {
      currentAnswer.dropQuestion();
    }

    failAnswer(TIMEOUT_MESSAGE);
  };

  const { restart: restartTimeout } = useAnswerTimeout({ isRunning: isGenerating, onTimeout });

  const finishBackgroundAnswer = (chatId: string): void => {
    backgroundChatIds.delete(chatId);

    if (chatId === chatIdRef.current) {
      setIsGenerating(false);
      onBackgroundAnswerDone(chatId);
    }
  };

  const acceptEvent = ({ chatId, messageId }: IAnswerEvent): boolean => {
    const { placeholderId } = currentAnswer;
    const isWaitingForNewChatId = !!placeholderId && !chatIdRef.current;

    if (backgroundChatIds.has(chatId)) {
      return false;
    }

    if (chatId !== chatIdRef.current && currentAnswer.skipDroppedReply()) {
      backgroundChatIds.add(chatId);
      onChatCreated();

      return false;
    }

    if (chatId !== chatIdRef.current && !isWaitingForNewChatId) {
      return false;
    }

    if (placeholderId) {
      setChatId(chatId);
    }

    if (messageId && placeholderId) {
      currentAnswer.replacePlaceholder(messageId);
      setMessages((prev) => renamePlaceholder(prev, placeholderId, messageId));
    } else if (messageId && !currentAnswer.hasMessage(messageId)) {
      currentAnswer.addMessage(messageId);
      setMessages((prev) => addAssistantMessage(prev, messageId));
    }

    return true;
  };

  const onRejectedError = (errorText: string): void => {
    if (!currentAnswer.hasDroppedQuestions()) {
      failAnswer(errorText);
    }
  };

  const onRejectedDone = (): void => {
    if (!currentAnswer.skipDroppedReply()) {
      finishAnswer();
    }
  };

  const onProcessing = (payload: IAskAiProcessingPayload): void => {
    const messageId = payload.messageId ?? currentAnswer.placeholderId;

    if (!messageId || !acceptEvent(payload)) {
      return;
    }

    restartTimeout();
    setMessages((prev) => applyProcessing(prev, messageId, payload));
  };

  const onAnswer = (payload: IAskAiAnswerPayload): void => {
    if (!acceptEvent(payload)) {
      return;
    }

    restartTimeout();

    const text = currentAnswer.addChunk(payload.messageId, payload.seq, payload.content);

    setMessages((prev) => applyAnswer(prev, payload.messageId, text));
  };

  const onError = ({ chatId, messageId, message }: IAskAiErrorPayload): void => {
    if (!chatId || !messageId) {
      onRejectedError(message);
      return;
    }

    if (acceptEvent({ chatId, messageId })) {
      setMessages((prev) => applyError(prev, messageId, message));
    }
  };

  const onDone = ({ chatId }: IAskAiDonePayload): void => {
    if (!chatId) {
      onRejectedDone();
      return;
    }

    if (!acceptEvent({ chatId })) {
      finishBackgroundAnswer(chatId);
      return;
    }

    const messageIds = currentAnswer.getMessageIds();

    setMessages((prev) => applyDone(prev, messageIds));
    finishAnswer();
  };

  const onCancelled = ({ chatId, cancelled }: IAskAiCancelledPayload): void => {
    if (backgroundChatIds.has(chatId)) {
      finishBackgroundAnswer(chatId);
      return;
    }

    if (chatId !== chatIdRef.current) {
      return;
    }

    if (cancelled) {
      const messageIds = currentAnswer.getMessageIds();

      setMessages((prev) => applyCancelled(prev, messageIds));
    }

    finishAnswer();
  };

  const onConnectionError = (message: string): void => {
    backgroundChatIds.clear();
    setConnectionError(message);
    failAnswer(message);
  };

  const stopOnUnsubscribe = (): void => {
    cancel();
    currentAnswer.stop();
  };

  useAskAiChatSubscription({
    isEnabled,
    handlers: { onProcessing, onAnswer, onError, onDone, onCancelled, onConnectionError },
    onBeforeUnsubscribe: stopOnUnsubscribe,
  });

  const send = useCallback((question: string): void => {
    const text = question.trim();

    if (!text || isAnswerInProgress()) {
      return;
    }

    const placeholderId = uuid();

    setConnectionError(null);
    setMessages((prev) => appendQuestion(prev, text, placeholderId));
    currentAnswer.start({ placeholderId, isNewChat: !chatIdRef.current });
    setIsGenerating(true);
    restartTimeout();

    subscription.ask({
      message: text,
      chatId: chatIdRef.current ?? undefined,
      limit: ROWS_PER_ANSWER,
      promptVersionId: promptVersionIdRef.current,
    });
  }, [
    chatIdRef, promptVersionIdRef, currentAnswer, isAnswerInProgress,
    restartTimeout, setConnectionError, setMessages, subscription,
  ]);

  const switchChat = useCallback((nextChatId: string | null): void => {
    const chatId = chatIdRef.current;

    if (currentAnswer.isRunning && chatId) {
      backgroundChatIds.add(chatId);
    } else {
      currentAnswer.dropQuestion();
    }

    finishAnswer();
    setIsGenerating(!!nextChatId && backgroundChatIds.has(nextChatId));
    setConnectionError(null);
  }, [backgroundChatIds, chatIdRef, currentAnswer, finishAnswer, setConnectionError]);

  return { isGenerating, send, cancel, switchChat };
};

export default useAskAiChatAnswer;
